import React, { useEffect, useState } from "react";
import LoadingPanel from "@/components/loadingPanel";
import { getAuthToken } from "./chatAuth";
import { AiChatSession } from "./aiChatSession";
import { ConversationSelector } from "./conversationSelector";

const AiChat = ({ onClose, companyId }) => {
  const [history, setHistory] = useState({
    status: "loading",
    conversationId: null,
    conversationTitle: null,
    messages: [],
    totalCostSEK: 0,
  });
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const [isConversationSelectorOpen, setIsConversationSelectorOpen] =
    useState(false);

  useEffect(() => {
    if (!companyId) {
      return;
    }

    let cancelled = false;

    const loadHistory = async () => {
      setHistory({
        status: "loading",
        conversationId: null,
        conversationTitle: null,
        messages: [],
        totalCostSEK: 0,
      });

      try {
        const response = await fetch("/api/ai-agent/chat/latest", {
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
            "x-company-id": companyId,
          },
        });

        if (!response.ok) {
          throw new Error("Could not load conversation history");
        }

        const data = await response.json();
        if (cancelled) {
          return;
        }

        setHistory({
          status: "ready",
          conversationId: data.conversation?.id ?? null,
          conversationTitle: data.conversation?.title ?? null,
          messages: data.messages ?? [],
          totalCostSEK: data.conversation?.totalCostSEK ?? 0,
        });
      } catch (error) {
        console.error("Could not load conversation history:", error);
        if (!cancelled) {
          setHistory({
            status: "error",
            conversationId: null,
            conversationTitle: null,
            messages: [],
            totalCostSEK: 0,
          });
        }
      }
    };

    void loadHistory();

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const refreshConversationCost = async (conversationId) => {
    try {
      const response = await fetch(
        `/api/ai-agent/conversations/${conversationId}`,
        {
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
            "x-company-id": companyId,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Could not load conversation cost");
      }

      const data = await response.json();
      setHistory((currentHistory) => {
        if (
          currentHistory.conversationId &&
          currentHistory.conversationId !== conversationId
        ) {
          return currentHistory;
        }

        return {
          ...currentHistory,
          conversationTitle:
            data.conversation.title ?? currentHistory.conversationTitle,
          totalCostSEK: data.conversation.totalCostSEK,
        };
      });
    } catch (error) {
      console.error("Could not load conversation cost:", error);
    }
  };

  const createNewConversation = async () => {
    if (isCreatingConversation) {
      return;
    }

    setIsCreatingConversation(true);

    try {
      const response = await fetch("/api/ai-agent/conversations", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          "x-company-id": companyId,
        },
      });

      if (!response.ok) {
        throw new Error("Could not create a new conversation");
      }

      const data = await response.json();
      setHistory({
        status: "ready",
        conversationId: data.conversation.id,
        conversationTitle: data.conversation.title ?? null,
        messages: [],
        totalCostSEK: data.conversation.totalCostSEK,
      });
    } catch (error) {
      console.error("Could not create a new conversation:", error);
    } finally {
      setIsCreatingConversation(false);
    }
  };

  const selectConversation = async (conversationId) => {
    if (conversationId === history.conversationId) {
      setIsConversationSelectorOpen(false);
      return;
    }

    setIsConversationSelectorOpen(false);

    try {
      const response = await fetch(
        `/api/ai-agent/conversations/${conversationId}`,
        {
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
            "x-company-id": companyId,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Could not load conversation history");
      }

      const data = await response.json();
      setHistory({
        status: "ready",
        conversationId: data.conversation.id,
        conversationTitle: data.conversation.title ?? null,
        messages: data.messages ?? [],
        totalCostSEK: data.conversation.totalCostSEK,
      });
    } catch (error) {
      console.error("Could not load selected conversation:", error);
    }
  };

  if (!companyId || history.status === "loading") {
    return <LoadingPanel />;
  }

  if (history.status === "error") {
    return (
      <div className="text-destructive flex h-full items-center justify-center text-center">
        Could not load conversation history.
      </div>
    );
  }

  return (
    <>
      <AiChatSession
        key={`${companyId}:${history.conversationId ?? "new"}`}
        onClose={onClose}
        companyId={companyId}
        initialMessages={history.messages}
        initialConversationId={history.conversationId}
        conversationTitle={history.conversationTitle}
        conversationCostSEK={history.totalCostSEK}
        onConversationFinished={refreshConversationCost}
        onClearConversation={createNewConversation}
        isCreatingConversation={isCreatingConversation}
        onOpenConversations={() => setIsConversationSelectorOpen(true)}
      />
      {isConversationSelectorOpen && (
        <ConversationSelector
          companyId={companyId}
          onClose={() => setIsConversationSelectorOpen(false)}
          onSelect={selectConversation}
        />
      )}
    </>
  );
};

export { AiChat };
